import React from "react";
import { Grid, TextField, Paper, Typography, IconButton, Button, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterCard from "./FilterCard";
import { FilterModalStyles } from "./styles/FilterModal.styles";
import { FilterModalProps, FilterItems2 } from "./types";
import CrossIcon from "src/icons/CrossIcon";
import SectorSelection from "./SectorSelection";
import { trackEvent } from "src/Amplitude";
import api from "src/store/features/api";

const ERROR_MESSAGE = "Alphabetical characters are only accepted";

const FILTER_ITEMS = {
  primary: [],
  secondary: [],
};

const FILTER_SELCTORS = ["Primary sectors", "Secondary sectors"];

const FilterModal = (props: FilterModalProps) => {
  const { onClose, setValue, title } = props;
  const classes = FilterModalStyles();

  /*
  ==================== HOOKS ======================
  */

  const [search, setSearch] = React.useState("");
  const [sectors, setSectors] = React.useState<FilterItems2>(FILTER_ITEMS);
  const [virtualFilter, setVirtualFilter] = React.useState<string[]>([]);
  const [filter, setFilter] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string>("");
  const [selector, setSelector] = React.useState<string>(FILTER_SELCTORS[0]);
  const theme = useTheme();
  React.useEffect(() => {
    trackEvent("Visi_Page", {
      page: "Markets",
    });
    fetchSectors();
  }, []);

  React.useEffect(() => {
    handleSearch();
  }, [search]);

  /*
  ==================== FUNCTIONS ======================
  */

  const [getSectors] = api.useLazyGetCryptoSectorsQuery();
  // fetch sectors from endpoint
  const fetchSectors = async () => {
    try {
      const data = await getSectors().unwrap();
      const newSectorObj = {
        primary: data.primary,
        secondary: data.secondary,
      };
      setSectors(newSectorObj);
      setVirtualFilter([...data.primary, ...data.secondary]);
    } catch (error) {
      console.warn(error);
    }
  };

  // main search function
  const handleSearch = () => {
    // IF --search-- IS EMPTY, RUN THIS CODE AND RESET FILTER
    if (search.length <= 0) {
      setFilter([]);
      return;
    }
    // FILTER ARRAY HERE, RETURN ITEM THAT MATCHES --search--
    const filter = virtualFilter.filter((item: string) => {
      return item.toLowerCase().match(search.toLowerCase());
    });
    setFilter(filter);
  };

  // assess input and set search hook
  const validateInput = (e: any) => {
    if (!e.target.value.match(/^[a-zA-Z0-9 ]+$/)) {
      if (search.length === 1) {
        setSearch("");
      } else {
        setError(ERROR_MESSAGE);
        e.target.value = search;
      }
    } else {
      setError("");
      setSearch(e.target.value);
    }
  };

  // onChange function
  const handleOnChange = (e: any) => {
    validateInput(e);
  };

  return (
    <Paper className={classes.container}>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        className={classes.crossContainer}>
        {/* header */}
        <Typography className={classes.heading}>{title}</Typography>
        <IconButton onClick={onClose}>
          <CrossIcon color={theme.palette.common.black} width="20" height="20" />
        </IconButton>
      </Grid>
      {/* searchbar */}
      <TextField
        fullWidth
        variant="outlined"
        value={search}
        onChange={handleOnChange}
        helperText={error}
        error={!!error}
        InputProps={{
          startAdornment: (
            <SearchIcon
              fontSize="large"
              color="disabled"
              classes={{ root: classes.adornedStart }}
            />
          ),
        }}
        placeholder="Search"
        className={classes.searchBar}
      />

      <Grid
        item
        container
        justifyContent="flex-end"
        sx={{ paddingRight: "22px", paddingBottom: "10px" }}
        onClick={() =>
          trackEvent("Select_Markets_Page", {
            option: "Select_Sector_Clear_Filter",
          })
        }>
        <Button
          disableRipple
          variant="outlined"
          sx={{
            textTransform: "none",
            fontSize: "12px",
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.text.primary}`,
          }}
          onClick={() => setValue("")}>
          Clear filter
        </Button>
      </Grid>

      <Grid item xs={12}>
        <SectorSelection selectors={FILTER_SELCTORS} selection={selector} onClick={setSelector} />
      </Grid>
      {search.length <= 0 ? (
        <FilterCard
          items={selector === FILTER_SELCTORS[0] ? sectors.primary : sectors.secondary}
          setValue={setValue}
        />
      ) : (
        filter.map((el) => (
          <Grid key={el} item xs={12} container>
            <Grid item xs={12} className={classes.itemList} onClick={() => setValue(el)}>
              <Typography className={classes.item}>{el}</Typography>
            </Grid>
          </Grid>
        ))
      )}
    </Paper>
  );
};

export default React.memo(FilterModal);
